import "./booking-dialog.scss";

import { MessageResponseDto } from "@api/api";
import { useUserIdentityContext } from "@core/services/user-identity.context";
import { getFormErrorMessage } from "@core/utility/get-error-message";
import FormPatient from "@domain/patient/form-patient/form-patient";
import {
  patientFormDefault,
  registrationFormDefault,
} from "@factory/form/form.default";
import {
  PatientFormRule,
  PatientFormSchema,
  RegistrationFormRule,
  RegistrationFormSchema,
} from "@factory/form/form.rule";
import { GetPatientModel } from "@factory/model/query-model-patient";
import {
  createRegistrationController,
  QueryKeyRegistration,
} from "@factory/model/query-model-registration";
import { useQyCreatePatient } from "@factory/query/patient.query";
import { useQyCreateRegistration } from "@factory/query/registration.query";
import { useQyGetTenantById } from "@factory/query/tenant.query";
import { useQyGetUtilService } from "@factory/query/util-service.query";
import { useFormApiMapper } from "@factory/services/form-api-mapper.hook";
import { zodResolver } from "@hookform/resolvers/zod";
import { bookingOptions } from "@shared/data/booking-options";
import { LabelValue } from "@shared/models/label-value.interface";
import InputDateControl from "@shared/ui/hook-form/input-date-control/input-date-control";
import InputTextareaControl from "@shared/ui/hook-form/input-textarea-control/input-textarea-control";
import { useNotificationContext } from "@shared/ui/notification/notification.context";
import TextBlock from "@shared/ui/text-block/text-block";
import { useDateTimeContext } from "@shared/utility/date-time-service/date-time.context";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { MultiSelect } from "primereact/multiselect";
import { ProgressBar } from "primereact/progressbar";
import { useCallback, useEffect, useRef, useState } from "react";
import { FieldErrors, FormProvider, useForm } from "react-hook-form";
import { useQueryClient } from "react-query";
import { useParams } from "react-router-dom";

import PatientSearch from "../patient-search/patient-search";

interface BookingDialogProps {
  visible: boolean;
  selectedDate: string;
  onHide: () => void;
  onBookingCreated: () => void;
}

export function BookingDialog({
  visible,
  selectedDate,
  onHide,
  onBookingCreated,
}: BookingDialogProps) {
  const [selectedPatient, setSelectedPatient] =
    useState<GetPatientModel | null>(null);
  const [mode, setMode] = useState<"search" | "create">("search");
  const [selectedServices, setSelectedServices] = useState<string[]>([]);
  const [selectedServiceTypes, setSelectedServiceTypes] = useState<string[]>(
    [],
  );
  const [isSubmittingMultiple, setIsSubmittingMultiple] = useState(false);
  const [bookingProgress, setBookingProgress] = useState({
    total: 0,
    completed: 0,
    failed: 0,
  });
  const submissionAbortedRef = useRef(false);
  const queryClient = useQueryClient();

  const { mapCreateRegistration, mapCreatePatient } = useFormApiMapper();
  const { showError, showSuccess } = useNotificationContext();
  const { formatDateForInput } = useDateTimeContext();
  const { client: tenantSlug } = useParams<{ client: string }>();
  const { getUserRole } = useUserIdentityContext();
  const { tenant } = getUserRole();

  // Fetch tenant data to check show_services_in_public_booking
  const { data: tenantResponse } = useQyGetTenantById(tenant || "");
  const tenantData = tenantResponse?.data?.[0];
  const showServicesInPublicBooking = Boolean(
    tenantData?.show_services_in_public_booking,
  );

  // Get services from util service
  const { data: utilServiceResponse, isLoading: isFetchingServices } =
    useQyGetUtilService(
      "",
      999,
      0,
      undefined,
      undefined,
      visible && showServicesInPublicBooking,
    );
  const utilServiceData = utilServiceResponse?.data || [];
  const serviceOptions = utilServiceData.map(
    (item) =>
      ({
        label: item.name,
        value: item.code,
      }) as LabelValue,
  );

  // Form setup for registration
  const formMethod = useForm<RegistrationFormSchema>({
    defaultValues: registrationFormDefault,
    resolver: zodResolver(RegistrationFormRule),
  });
  const { handleSubmit, setValue, reset } = formMethod;

  // Form setup for patient creation
  const patientFormMethod = useForm<PatientFormSchema>({
    defaultValues: patientFormDefault,
    resolver: zodResolver(PatientFormRule),
  });
  const { handleSubmit: handlePatientSubmit, reset: resetPatientForm } =
    patientFormMethod;

  // API Create Patient
  const handlePatientCreateSuccess = (response: MessageResponseDto) => {
    showSuccess("Patient created successfully");
    const createdData = response.data as Record<string, unknown>;

    // Convert camelCase response from create API to snake_case to match GetPatientModel
    const newPatient: GetPatientModel = {
      code: createdData?.code,
      first_name: (createdData?.firstName as string) || "",
      middle_name: (createdData?.middleName as string) || "",
      last_name: (createdData?.lastName as string) || "",
      gender: (createdData?.gender as string) || "",
      address: (createdData?.address as string) || "",
      mobile_number: (createdData?.mobileNumber as string) || "",
      email: (createdData?.email as string) || "",
      birthday: (createdData?.birthday as string) || "",
      is_taking_medication:
        (createdData?.isTakingMedication as boolean) || false,
      is_alergic_to_vitamins: (createdData?.hasAllergies as boolean) || false,
      has_past_hospitalization:
        (createdData?.hasMedicalConditions as boolean) || false,
      past_hospitalization_reason:
        (createdData?.medicalConditionDetails as string) || "",
      tenant: (createdData?.tenant as string) || "",
    } as GetPatientModel;

    // Switch to search mode and select the newly created patient
    setMode("search");
    resetPatientForm();
    handlePatientSelect(newPatient);
  };
  const { mutate: createPatient, isLoading: isCreatingPatient } =
    useQyCreatePatient(handlePatientCreateSuccess);

  // API Create Registration (used for single-service booking)
  const handleApiSuccess = () => {
    showSuccess("Booking created successfully");
    resetBookingState();
    onBookingCreated();
    onHide();
  };
  const { mutate: createRegistration, isLoading: isCreating } =
    useQyCreateRegistration(handleApiSuccess);

  // Multi-service booking handler - calls API directly to avoid hook-level handlers
  const handleMultiServiceBooking = useCallback(
    async (form: RegistrationFormSchema, services: string[]) => {
      setIsSubmittingMultiple(true);
      submissionAbortedRef.current = false;
      const total = services.length;
      setBookingProgress({ total, completed: 0, failed: 0 });

      const failedServices: string[] = [];
      let completed = 0;

      for (const service of services) {
        if (submissionAbortedRef.current) break;

        const updatedForm = {
          ...form,
          tenant: tenantSlug || "",
          service: showServicesInPublicBooking ? service : "",
          serviceType: showServicesInPublicBooking ? "" : service,
        };
        const formData = mapCreateRegistration(updatedForm);

        try {
          const operation = await createRegistrationController(formData);
          await operation();
          completed++;
          setBookingProgress((prev) => ({
            ...prev,
            completed: prev.completed + 1,
          }));
        } catch {
          failedServices.push(service);
          setBookingProgress((prev) => ({
            ...prev,
            failed: prev.failed + 1,
          }));
        }
      }

      setIsSubmittingMultiple(false);
      queryClient.invalidateQueries(QueryKeyRegistration);

      if (failedServices.length === 0) {
        showSuccess(`All ${total} service(s) booked successfully`);
        resetBookingState();
        onBookingCreated();
        onHide();
      } else if (completed > 0) {
        const failedLabels = failedServices
          .map((code) => {
            const opt = serviceOptions.find((o) => o.value === code);
            return opt?.label || code;
          })
          .join(", ");
        showError(
          `${completed} of ${total} bookings created. Failed: ${failedLabels}`,
        );
        onBookingCreated();
      } else {
        showError("All bookings failed. Please try again.");
      }
    },
    [
      tenantSlug,
      showServicesInPublicBooking,
      mapCreateRegistration,
      showSuccess,
      showError,
      onBookingCreated,
      onHide,
      serviceOptions,
    ],
  );

  // Set the selected date when dialog opens
  useEffect(() => {
    if (visible && selectedDate) {
      setValue("date", formatDateForInput(selectedDate));
    }
  }, [visible, selectedDate, setValue, formatDateForInput]);

  // Handle patient selection
  const handlePatientSelect = (patient: GetPatientModel | null) => {
    setSelectedPatient(patient);

    if (patient) {
      // Populate form with patient data
      setValue("firstName", patient.first_name || "");
      setValue("middleName", patient.middle_name || "");
      setValue("lastName", patient.last_name || "");
      setValue("gender", patient.gender || "");
      setValue("address", patient.address || "");
      setValue("mobileNumber", patient.mobile_number || "");
      setValue("email", patient.email || "");

      if (patient.birthday) {
        setValue("birthday", formatDateForInput(patient.birthday));
      }

      // Set default values for medical info from patient data if available
      setValue(
        "isTakingMedication",
        Boolean(patient.is_taking_medication) || false,
      );
      setValue(
        "hasAllergies",
        Boolean(patient.is_alergic_to_vitamins) || false,
      );
      setValue(
        "hasMedicalConditions",
        Boolean(patient.has_past_hospitalization) || false,
      );
      setValue(
        "medicalConditonDetails",
        patient.past_hospitalization_reason || "",
      );
    }
  };

  const handleValidate = (form: RegistrationFormSchema) => {
    const services = showServicesInPublicBooking
      ? selectedServices
      : selectedServiceTypes;

    if (services.length === 0) {
      showError("Please select at least one service");
      return;
    }

    if (services.length === 1) {
      // Single service - use existing flow
      const updatedForm = {
        ...form,
        tenant: tenantSlug || "",
        service: showServicesInPublicBooking ? services[0] : "",
        serviceType: showServicesInPublicBooking ? "" : services[0],
      };
      const formData = mapCreateRegistration(updatedForm);
      createRegistration(formData);
    } else {
      // Multiple services - use sequential booking
      handleMultiServiceBooking(form, services);
    }
  };

  const handleValidateError = (err: FieldErrors<RegistrationFormSchema>) => {
    const formMessage = getFormErrorMessage(err);
    showError(formMessage);
  };

  const handlePatientValidate = (form: PatientFormSchema) => {
    const updatedForm = {
      ...form,
      tenant: tenant || "",
    };
    const formData = mapCreatePatient(updatedForm);
    createPatient(formData);
  };

  const handlePatientValidateError = (err: FieldErrors<PatientFormSchema>) => {
    const formMessage = getFormErrorMessage(err);
    showError(formMessage);
  };

  const resetBookingState = () => {
    reset();
    setSelectedPatient(null);
    setSelectedServices([]);
    setSelectedServiceTypes([]);
    setBookingProgress({ total: 0, completed: 0, failed: 0 });
  };

  const handleClose = () => {
    if (isSubmittingMultiple) {
      submissionAbortedRef.current = true;
    }
    resetBookingState();
    resetPatientForm();
    setMode("search");
    onHide();
  };

  const renderPatientInfo = () => {
    if (!selectedPatient) {
      return null;
    }

    return (
      <div className="patient-info-section bg-gray-50 p-4 rounded-lg mb-4">
        <h4 className="text-md font-semibold mb-3 text-slate-blue-custom">
          Patient Information
        </h4>
        <div className="grid grid-cols-2 gap-2 text-sm">
          <TextBlock
            label="Name"
            value={`${selectedPatient.first_name} ${
              selectedPatient.middle_name || ""
            } ${selectedPatient.last_name}`}
            mode="horizontal"
          />
          <TextBlock
            label="Gender"
            value={selectedPatient.gender}
            mode="horizontal"
          />
          <TextBlock
            label="Mobile"
            value={selectedPatient.mobile_number}
            mode="horizontal"
          />
          <TextBlock
            label="Email"
            value={selectedPatient.email}
            mode="horizontal"
          />
          <TextBlock
            label="Address"
            value={selectedPatient.address}
            mode="horizontal"
            className="col-span-2"
          />
        </div>
      </div>
    );
  };

  const dialogFooter = (
    <div className="flex gap-2 justify-end">
      <Button
        label="Cancel"
        icon="pi pi-times"
        className="p-button-text"
        onClick={handleClose}
        disabled={isCreating || isCreatingPatient || isSubmittingMultiple}
      />
      {mode === "create" ? (
        <Button
          label="Create Patient"
          icon="pi pi-user-plus"
          loading={isCreatingPatient}
          onClick={handlePatientSubmit(
            handlePatientValidate,
            handlePatientValidateError,
          )}
        />
      ) : (
        <Button
          label={
            (showServicesInPublicBooking
              ? selectedServices
              : selectedServiceTypes
            ).length > 1
              ? `Book ${(showServicesInPublicBooking ? selectedServices : selectedServiceTypes).length} Services`
              : "Book Appointment"
          }
          icon="pi pi-check"
          loading={isCreating || isSubmittingMultiple}
          onClick={handleSubmit(handleValidate, handleValidateError)}
          disabled={!selectedPatient || isSubmittingMultiple}
        />
      )}
    </div>
  );

  return (
    <Dialog
      header={mode === "search" ? "Book Appointment" : "Create New Patient"}
      visible={visible}
      onHide={handleClose}
      style={{ width: mode === "create" ? "800px" : "600px" }}
      modal
      className="booking-dialog"
      footer={dialogFooter}
    >
      <div className="booking-dialog-content">
        {/* Mode Toggle */}
        <div className="mb-4 flex gap-2 items-center justify-between border-b pb-3">
          <div className="flex gap-2">
            <Button
              label="Search Patient"
              icon="pi pi-search"
              className={`p-button-sm ${
                mode === "search" ? "" : "p-button-text"
              }`}
              onClick={() => setMode("search")}
              disabled={isCreatingPatient}
            />
            <Button
              label="Create Patient"
              icon="pi pi-user-plus"
              className={`p-button-sm ${
                mode === "create" ? "" : "p-button-text"
              }`}
              onClick={() => setMode("create")}
              disabled={isCreatingPatient}
            />
          </div>
        </div>

        {/* Search Patient Mode */}
        {mode === "search" && (
          <>
            <div className="mb-4">
              <h4 className="text-md font-semibold mb-2 text-slate-blue-custom">
                Search Patient
              </h4>
              <PatientSearch onPatientSelect={handlePatientSelect} />
            </div>

            {/* Selected Patient Info */}
            {renderPatientInfo()}

            {/* Booking Details */}
            {selectedPatient && (
              <FormProvider {...formMethod}>
                <div className="booking-details-section">
                  <h4 className="text-md font-semibold mb-3 text-slate-blue-custom">
                    Appointment Details
                  </h4>
                  <div className="grid grid-cols-2 gap-4">
                    <InputDateControl<RegistrationFormSchema>
                      control={formMethod.control}
                      name="date"
                      label="Appointment Date"
                      className="w-full"
                      containerClassName="pb-2"
                      placeholder="Select date"
                    />
                    {/* <DropdownControl<RegistrationFormSchema>
                  control={formMethod.control}
                  name="time"
                  label="Preferred Time"
                  className="w-full"
                  containerClassName="pb-2"
                  options={bookingOptions}
                  placeholder="Select time"
                /> */}
                    <InputDateControl<RegistrationFormSchema>
                      type="time"
                      control={formMethod.control}
                      name="time"
                      label="Preferred Time"
                      className="w-full"
                      containerClassName="pb-2"
                      placeholder="Enter time"
                    />
                  </div>
                  {/* <DropdownControl<RegistrationFormSchema>
                control={formMethod.control}
                name="service"
                label="Service"
                className="w-full"
                containerClassName="pb-2"
                options={serviceOptions}
                placeholder={
                  isFetchingServices
                    ? "Loading services..."
                    : "Select a service"
                }
              /> */}
                  {showServicesInPublicBooking ? (
                    <div className="field mt-5 pb-2">
                      <label className="text-xs text-gray-500 block mb-1">
                        Select Services
                      </label>
                      <MultiSelect
                        value={selectedServices}
                        options={serviceOptions}
                        onChange={(e) => setSelectedServices(e.value || [])}
                        placeholder={
                          isFetchingServices
                            ? "Loading services..."
                            : "Select one or more services"
                        }
                        filter
                        display="chip"
                        maxSelectedLabels={3}
                        className="w-full"
                        disabled={isSubmittingMultiple}
                      />
                      {selectedServices.length > 1 && (
                        <small className="text-blue-600 mt-1 block">
                          <i className="pi pi-info-circle mr-1"></i>
                          {selectedServices.length} services selected — each
                          will be booked individually
                        </small>
                      )}
                    </div>
                  ) : (
                    <div className="field mt-5 pb-2">
                      <label className="text-xs text-gray-500 block mb-1">
                        Select Services
                      </label>
                      <MultiSelect
                        value={selectedServiceTypes}
                        options={bookingOptions}
                        onChange={(e) => setSelectedServiceTypes(e.value || [])}
                        placeholder="Select one or more services"
                        display="chip"
                        maxSelectedLabels={3}
                        className="w-full"
                        disabled={isSubmittingMultiple}
                      />
                      {selectedServiceTypes.length > 1 && (
                        <small className="text-blue-600 mt-1 block">
                          <i className="pi pi-info-circle mr-1"></i>
                          {selectedServiceTypes.length} services selected — each
                          will be booked individually
                        </small>
                      )}
                    </div>
                  )}
                  {/* Multi-service booking progress */}
                  {isSubmittingMultiple && (
                    <div className="mb-3 p-3 bg-blue-50 rounded-lg">
                      <div className="flex justify-between text-sm mb-2">
                        <span>
                          Booking services... (
                          {bookingProgress.completed + bookingProgress.failed} /{" "}
                          {bookingProgress.total})
                        </span>
                        {bookingProgress.failed > 0 && (
                          <span className="text-red-600">
                            {bookingProgress.failed} failed
                          </span>
                        )}
                      </div>
                      <ProgressBar
                        value={Math.round(
                          ((bookingProgress.completed +
                            bookingProgress.failed) /
                            bookingProgress.total) *
                            100,
                        )}
                        showValue={false}
                        style={{ height: "8px" }}
                      />
                    </div>
                  )}
                  <InputTextareaControl<RegistrationFormSchema>
                    control={formMethod.control}
                    name="otherRemarks"
                    label="Additional Remarks"
                    className="w-full"
                    containerClassName="pb-2"
                    placeholder="Enter any additional notes"
                  />
                </div>
              </FormProvider>
            )}

            {!selectedPatient && (
              <div className="text-center text-gray-500 py-8">
                <i className="pi pi-user text-4xl mb-3 block"></i>
                <p>Search and select a patient to book an appointment</p>
              </div>
            )}
          </>
        )}

        {/* Create Patient Mode */}
        {mode === "create" && (
          <FormProvider {...patientFormMethod}>
            <div className="create-patient-section">
              <h4 className="text-md font-semibold mb-3 text-slate-blue-custom">
                New Patient Information
              </h4>
              <div className="max-h-96 overflow-y-auto pr-2">
                <FormPatient simplifiedMode={true} />
              </div>
            </div>
          </FormProvider>
        )}
      </div>
    </Dialog>
  );
}

export default BookingDialog;
